import React, { useState, useCallback, useEffect } from 'react';
import { doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { seedOfficialDatabase } from './seedDatabase';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebase';
import { signInWithCustomToken } from 'firebase/auth';

import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ResetPassword from './pages/Auth/ResetPassword';
import GameSlots from './components/GameSlots';
import Dashboard from './components/Dashboard';
import TopBar from './components/TopBar';
import Settings from './components/Settings';
import HallOfFameSequence from './components/HallOfFameSequence';
import AccessRequired from './components/AccessRequired';
import { useLegal, LegalFooter } from './components/LegalPages';
import { useAccess } from './useAccess';
import WelcomeBackPopup from './components/WelcomeBackPopup';
import WelcomeModal, { useWelcomeModal } from './components/WelcomeModal';
import TicketForm from './components/TicketForm';
import AdminPanel from './components/AdminPanel';
import { useTicketUpdates } from './useTicketUpdates';

import { CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#d4af37' },
    secondary: { main: '#c0392b' },
    background: { default: '#0a0a0a', paper: '#1a1a1a' },
  },
  typography: { fontFamily: "'Monda', sans-serif" },
});

// ── MobileGate : bloque les vrais appareils mobiles/tablettes ─────────
const logoUrl = process.env.PUBLIC_URL + '/logo-header.png';

function isMobileDevice() {
  // Détecte les vrais appareils mobiles/tablettes via userAgent
  // Un desktop avec petite fenêtre n'est PAS bloqué
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(navigator.userAgent);
}

function MobileGate({ children }) {
  const [blocked] = useState(isMobileDevice);

  if (!blocked) return children;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#0a0a0a',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px', textAlign: 'center',
    }}>
      <img src={logoUrl} alt="GM Rivalry Engine" style={{ width: '90px', height: '90px', objectFit: 'contain', marginBottom: '28px', opacity: 0.9 }} onError={e => { e.target.style.display = 'none'; }} />
      <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '22px', letterSpacing: '4px', color: '#fff', marginBottom: '12px' }}>
        DESKTOP ONLY
      </div>
      <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '13px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.7', maxWidth: '280px', marginBottom: '32px' }}>
        GM Rivalry Engine is designed for desktop. Please open the app on a computer for the best experience.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '28px', height: '1px', background: 'rgba(224,16,16,0.5)' }} />
        <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(224,16,16,0.7)' }}>
          gmrivalryengine.com
        </div>
        <div style={{ width: '28px', height: '1px', background: 'rgba(224,16,16,0.5)' }} />
      </div>
    </div>
  );
}

// ── PrivateRoute : vérifie connexion + accès Patreon ─────────────────
const SESSION_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

function PrivateRoute({ children }) {
  const [user, loading] = useAuthState(auth);
  const accessStatus = useAccess(user, loading);

  // Tracker l'activité — met à jour lastActivity à chaque visite
  useEffect(() => {
    if (user) localStorage.setItem('gme_lastActivity', Date.now().toString());
  }, [user]);

  if (loading || accessStatus === 'loading') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.3)',
        fontFamily: 'Monda',
        fontSize: '0.7rem',
        letterSpacing: '2px',
      }}>
        LOADING...
      </div>
    );
  }

  if (!user) {
    // Vérifier si la session a expiré (> 7 jours d'inactivité)
    const lastActivity = parseInt(localStorage.getItem('gme_lastActivity') || '0', 10);
    const isExpired = lastActivity > 0 && (Date.now() - lastActivity) > SESSION_TIMEOUT_MS;
    localStorage.removeItem('gme_lastActivity');
    return <Navigate to={isExpired ? '/login?reason=session_expired' : '/login'} />;
  }

  if (accessStatus === 'inactive' || accessStatus === 'none') {
    return <AccessRequired />;
  }

  return children;
}

// Sanitize data before Firebase save - removes non-serializable values
function sanitizeForFirebase(obj) {
  try {
    var stripped = JSON.parse(JSON.stringify(obj, function(key, value) {
      if (typeof value === 'function') return undefined;
      if (typeof value === 'string' && value.startsWith('data:') && value.length > 50000) return '';
      if (key.startsWith('_') && key !== '_endOfSeason') return undefined;
      return value;
    }));
    return stripped;
  } catch(e) {
    console.error('sanitize error', e);
    return {};
  }
}

function MainApp({ onInGameChange }) {
  const [user] = useAuthState(auth);
  const accessStatus = useAccess(user, false);
  const isAdmin = accessStatus === 'admin';
  const [hasTicketUpdate, markTicketsSeen] = useTicketUpdates(user);
  const [activeGame, setActiveGame] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [seeding, setSeeding] = useState(false);
  const [seedDone, setSeedDone] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHofSequence, setShowHofSequence] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => { onInGameChange && onInGameChange(!!activeGame); }, [activeGame, onInGameChange]);

  // ── Welcome / Patch Notes modal ──────────────────────────────────
  const welcomeState = useWelcomeModal(user);   // null while loading
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [forcePatchNotes, setForcePatchNotes] = useState(false);
  const [forceWelcome, setForceWelcome] = useState(false);
  const showWelcomeModal = (!welcomeDismissed && welcomeState?.show === true) || forcePatchNotes || forceWelcome;
  const showTourTab = !forcePatchNotes && (forceWelcome || welcomeState?.showTourTab === true);

  var handleSeed = async function() {
    setSeeding(true);
    try {
      await seedOfficialDatabase(true);
      setSeedDone(true);
      alert('✅ Database mise à jour avec succès !');
    } catch(e) {
      alert('❌ Erreur: ' + e.message);
    } finally {
      setSeeding(false);
    }
  };

  React.useEffect(function() {
    var handleOnline = function() { setIsOnline(true); };
    var handleOffline = function() { setIsOnline(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return function() {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const [showWeekStart, setShowWeekStart] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [welcomeBackGame, setWelcomeBackGame] = useState(null);
  const [dashTab, setDashTab] = useState('home');
  const [goToSlots, setGoToSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Warning si l'utilisateur ferme l'onglet pendant un save en cours
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = 'Changes may not be saved yet. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus]);

  const handleHome = () => {
    if (activeGame) setDashTab('home');
    else { setGoToSlots(true); setTimeout(() => setGoToSlots(false), 100); }
  };

  const saveToFirebase = useCallback(async (gameData) => {
    if (!gameData?.id) return;
    setSaving(true);
    setSaveStatus('saving');
    try {
      const ref = doc(db, 'gameSlots', gameData.id);
      var s = sanitizeForFirebase({ ...gameData, _officialWrestlers: undefined });
      await updateDoc(ref, {
        roster: s.roster || [],
        champions: s.champions || {},
        fans: s.fans ?? 1000000,
        budget: s.budget,
        week: s.week || 1,
        rivalries: s.rivalries || [],
        rivals: s.rivals || [],
        lastWeek: gameData.lastWeek || [],
        seasonHistory: gameData.seasonHistory || [],
        bookingSlots: s.bookingSlots || [],
        bookingFinished: s.bookingFinished || false,
        tagTeams: s.tagTeams || [],
        hofActive: s.hofActive || [],
        hofGoalAnimShown: s.hofGoalAnimShown || false,
        pleBooking: s.pleBooking || {},
        pleMidCardSlots: s.pleMidCardSlots || [],
        nextPle: s.nextPle || null,
        season: s.season || 1,
        titleHistory: s.titleHistory || [],
        pastRivalries: s.pastRivalries || [],
        rivalryNotes: s.rivalryNotes || [],
        logistics: s.logistics || { arena: 1, crew: 1, sfx: 1, advertising: 1 },
        logisticsTokens: s.logisticsTokens ?? 0,
        logisticsOwned: s.logisticsOwned || { arena: [1], crew: [1], sfx: [1], advertising: [1] },
        matchType: s.matchType || 'classic',
        showResults: s.showResults || [],
        updatedAt: new Date(),
      });
    } catch (e) {
      console.error('Save error:', e);
      setSaving(false);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
      return;
    }
    setSaving(false);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 2500);
  }, []);

  const handleUpdateGame = useCallback((updatedGame) => {
    setActiveGame(updatedGame);
    saveToFirebase(updatedGame);
  }, [saveToFirebase]);

  const handleSave = () => activeGame && saveToFirebase(activeGame);

  const handleSaveAndQuit = async () => {
    if (activeGame) await saveToFirebase(activeGame);
    setActiveGame(null);
  };

  const handleQuit = () => setActiveGame(null);

  return (
    <>
      {!isOnline && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: 'rgba(231,76,60,0.95)', padding: '6px 16px', textAlign: 'center', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', letterSpacing: '1px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <span>⚠</span> NO INTERNET CONNECTION — Changes will not be saved until you reconnect
        </div>
      )}
      <TopBar
        onHome={handleHome}
        onSave={handleSave}
        onSaveAndQuit={handleSaveAndQuit}
        onQuit={handleQuit}
        onSettings={() => setShowSettings(true)}
        isInGame={!!activeGame}
        saveStatus={saveStatus}
        slotName={activeGame?.slotName || null}
        hasNewPatch={!welcomeDismissed && welcomeState?.show === true && welcomeState?.showTourTab === false}
        onPatchNotes={() => { setForcePatchNotes(true); setWelcomeDismissed(true); }}
        onReportBug={() => setShowBugReport(true)}
        isAdmin={isAdmin}
        onOpenAdmin={() => setShowAdminPanel(true)}
        settingsBadge={hasTicketUpdate}
      />
      {showSettings && (
        <Settings
          onClose={() => setShowSettings(false)}
          onSeed={handleSeed}
          seeding={seeding}
          seedDone={seedDone}
          gameData={activeGame || null}
          onUpdateGame={handleUpdateGame}
          onShowHofSequence={() => { setShowSettings(false); setShowHofSequence(true); }}
          onPatchNotes={() => { setShowSettings(false); setForcePatchNotes(true); setWelcomeDismissed(true); }}
          onTicketsSeen={markTicketsSeen}
        />
      )}
      {showHofSequence && activeGame && (
        <HallOfFameSequence
          gameData={activeGame}
          onClose={() => setShowHofSequence(false)}
        />
      )}
      {showBugReport && (
        <TicketForm onClose={() => setShowBugReport(false)} />
      )}
      {showAdminPanel && isAdmin && (
        <AdminPanel
          onClose={() => setShowAdminPanel(false)}
          onLaunchWelcome={() => { setForceWelcome(true); setForcePatchNotes(false); setWelcomeDismissed(true); }}
          onLaunchPatchNotes={() => { setForcePatchNotes(true); setWelcomeDismissed(true); }}/>
      )}
      {activeGame
        ? <Dashboard
            gameData={activeGame}
            activeTab={dashTab}
            setActiveTab={setDashTab}
            onBack={() => setActiveGame(null)}
            onUpdateGame={handleUpdateGame}
            showWeekStart={showWeekStart}
            onCloseWeekStart={() => setShowWeekStart(false)}
          />
        : <GameSlots
            onEnterGame={(game) => {
              setDashTab('home');
              setActiveGame(game);
              if (game._showWelcomeBack && (game.week || 1) > 1) {
                setWelcomeBackGame(game);
                setShowWelcomeBack(true);
              } else {
                setShowWeekStart(!!game._showWeekStart);
              }
            }}
            goToSlots={goToSlots}
          />
      }
      {showWelcomeBack && welcomeBackGame && (
        <WelcomeBackPopup
          gameData={welcomeBackGame}
          onEnter={() => {
            setShowWelcomeBack(false);
            setWelcomeBackGame(null);
          }}
        />
      )}
      {/* ── Welcome / Patch Notes modal ── */}
      {showWelcomeModal && (
        <WelcomeModal
          showTourTab={showTourTab}
          onClose={() => { setWelcomeDismissed(true); setForcePatchNotes(false); setForceWelcome(false); }}
        />
      )}
    </>
  );
}

function App() {
  const [user] = useAuthState(auth);
  const { openPage, modal: legalModal } = useLegal();
  const [inGame, setInGame] = useState(false);
  const [patreonLoginPending, setPatreonLoginPending] = useState(
    () => window.location.hash.includes('patreonLoginToken=')
  );
  const [patreonLoginError, setPatreonLoginError] = useState(null);

  useEffect(() => {
    if (!patreonLoginPending) return;
    const match = /patreonLoginToken=([^&]+)/.exec(window.location.hash);
    const token = match ? decodeURIComponent(match[1]) : null;
    // Nettoie le fragment tout de suite pour ne pas laisser le token traîner dans l'URL/l'historique
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
    if (!token) { setPatreonLoginPending(false); return; }

    let settled = false;
    // Filet de sécurité : si signInWithCustomToken ne résout jamais (Cloud Function
    // qui timeout, réseau qui traîne), on ne laisse pas l'utilisateur bloqué indéfiniment.
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      setPatreonLoginError('This is taking longer than expected. The sign-in link may have expired, or our server is slow to respond right now.');
      setPatreonLoginPending(false);
    }, 15000);

    signInWithCustomToken(auth, token)
      .then(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        setPatreonLoginPending(false);
      })
      .catch(err => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        console.error('Patreon login failed:', err);
        setPatreonLoginError('We couldn\'t sign you in with Patreon. Your link may have expired — please try connecting your account again.');
        setPatreonLoginPending(false);
      });

    return () => clearTimeout(timeoutId);
  }, [patreonLoginPending]);

  if (patreonLoginPending || patreonLoginError) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0a0a',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '18px', padding: '24px', textAlign: 'center',
      }}>
        {patreonLoginError ? (
          <>
            <div style={{ color: '#ff4444', fontFamily: 'Monda', fontSize: '0.75rem', letterSpacing: '0.5px', maxWidth: '360px', lineHeight: 1.6 }}>
              {patreonLoginError}
            </div>
            <button
              onClick={() => { setPatreonLoginError(null); window.location.href = '/login'; }}
              style={{
                fontFamily: 'Monda', letterSpacing: '1px', fontSize: '0.65rem',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.5)',
                color: '#fff', padding: '10px 24px', borderRadius: '4px', cursor: 'pointer',
              }}>
              BACK TO LOGIN
            </button>
          </>
        ) : (
          <div style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Monda', fontSize: '0.7rem', letterSpacing: '2px' }}>
            SIGNING IN WITH PATREON...
          </div>
        )}
      </div>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      {legalModal}
      <Router>
        <MobileGate>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<PrivateRoute><MainApp onInGameChange={setInGame} /></PrivateRoute>} />
          </Routes>
        </MobileGate>
      </Router>
      {!inGame && <LegalFooter onOpen={openPage} />}
    </ThemeProvider>
  );
}

export default App;
